
import React, { createContext, useContext, ReactNode, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ActionButtonMapping, AppState, BoardColumn, BoardConfig, ClientCompanyCode, DisplayFieldsGroup, ExtendedMoldOrderFormConfig, FeatureToggles, getAppState, MoldDisplayFieldConfig, RelatedOrderTypeConfig, SearchFieldConfig, StatusDueDateBehavior, UIConfig } from '../models/appState';
import { FormFieldConfig, Mold, MoldOrder, MoldOrderFirstLevelType, MoldOrderMainType, MoldOrderMainTypeStruct, MoldOrderStatus, MoldOrderType, OrderWorkflowDefinition, User, getMoldOrderFirstLevelType, getOrderTypeLabel, moldOrderTypesForRepair } from '../types/mold';
import { GenericColumn } from '../components/KanbanBoard/utils/tableColumns';
import { MoldDisplayFieldType } from '../models/appState';
import { KanbanCardHeadlines } from '@/types/kanban';
import { DisplayFieldConfig } from '@/types/form';
import { ProductPartsCell } from '@/components/MoldList/TableCell';
import { ROUTES } from '@/constants/routes';
import {
  defaultFields,
  fieldsForMoldAcceptance,
  fieldsForMinorRepair,
  fieldsForRepair,
  fieldsForModify,
  fieldsForMoldExport
} from '../utils/displayMoldOrderFields';
import { getDynamicIcon } from '@/utils/icon';


interface AppStateProviderProps {
  companyCode: ClientCompanyCode;
  children: ReactNode;
}

// Interface for mapping mold order types to their required feature toggles
interface MoldOrderTypeToggleMapping {
  [key: string]: keyof FeatureToggles;
}

// Mapping of mold order types to their required feature toggles
// This makes it easy to extend in the future - just add new mappings here
// Note: Both MoldOrderType.REPAIR_MAJOR and MoldOrderMainType.REPAIR_MAJOR have the same value 'repair.MAJOR'
const moldOrderTypeToggleMap: MoldOrderTypeToggleMapping = {
  [MoldOrderType.REPAIR_MAJOR]: 'enableMajorRepair', // This covers both MoldOrderType and MoldOrderMainType
  // NOTE: this map is shared by every company. A type listed here disappears from the
  // pickers of any company whose config leaves the toggle false/undefined — so both
  // sk.ts and sbi.ts must carry `enableMinorRepair`.
  [MoldOrderType.REPAIR_MINOR]: 'enableMinorRepair',
  [MoldOrderType.MODIFY]: 'enabledModifyOrder',
  // Add more mappings here as needed for future mold order types
  // Example: [MoldOrderType.SOME_NEW_TYPE]: 'enableSomeNewFeature',
};

// Simple helper to check if a type should be enabled based on feature toggles
const isTypeEnabled = (typeValue: string, featureToggles: FeatureToggles): boolean => {
  const requiredToggle = moldOrderTypeToggleMap[typeValue];
  return !requiredToggle || featureToggles[requiredToggle];
};

// Helper function to filter any array of mold order type options based on feature toggles
const filterMoldOrderTypesByToggle = <T extends { value: MoldOrderType }>(
  options: T[],
  featureToggles: FeatureToggles
): T[] => {
  return options.filter(option => isTypeEnabled(option.value, featureToggles));
};

// Helper function to filter MoldOrderMainTypeStruct based on feature toggles
const filterMoldOrderMainTypesByToggle = (
  mainTypes: MoldOrderMainTypeStruct[],
  featureToggles: FeatureToggles
): MoldOrderMainTypeStruct[] => {
  return mainTypes
    .map(mainType => ({
      ...mainType,
      subTypes: mainType.subTypes.filter(subType => isTypeEnabled(subType, featureToggles))
    }))
    .filter(mainType => {
      return mainType.subTypes.length > 0 && isTypeEnabled(mainType.value as any, featureToggles);
    });
};

export interface AppStateContextValue extends Omit<AppState, 'featureToggles'> {
  companyCode: ClientCompanyCode;
  featureToggles: FeatureToggles;
  getMoldFieldValue: (moldOrder: MoldOrder, field: MoldDisplayFieldType) => string;
  getMoldTitle: (mold: Mold) => string;
  getMoldStatusLabel: (mold: Mold) => string;
  getMoldDisplayFields: () => MoldDisplayFieldConfig;
  getMoldOrderCardHeadlines: (moldOrder: MoldOrder) => KanbanCardHeadlines;
  getFormFields: (moldOrderType: string) => FormFieldConfig[]; // Replace any[] with your FormFieldConfig[] type
  getFilteredMoldOrderTypesForRepair: () => typeof moldOrderTypesForRepair;
  getCurrentOrderTemplateVersion: (firstLevelType: MoldOrderFirstLevelType) => string;
  isInMoldOrderMainType: (type: MoldOrderMainType, subType: MoldOrderType) => boolean;
  getMoldOrderTypeLabel: (type: MoldOrderType) => string;
  isMoldOrderCheckedForFormCompletion: (orderType: MoldOrderType) => boolean;
  getMoldOrderMainType: (type: MoldOrderType | MoldOrderMainType) => MoldOrderFirstLevelType;
  getMoldOrderMainTypeLabel: (type: MoldOrderFirstLevelType) => string;
  getMoldOrderMainTypes: (firstLevelType: MoldOrderFirstLevelType) => MoldOrderMainTypeStruct[];
  getMoldFormConfiguration: () => Record<string, FormFieldConfig[]>;
  getOrderMainTypeLabel: (type: MoldOrderFirstLevelType, mainType: MoldOrderMainType) => string;
  getDisplayMoldOrderFields: (moldOrderType: MoldOrderType) => DisplayFieldConfig[];
  getStatusColor: (status: MoldOrderStatus, orderType: MoldOrderType, options?: { variant?: 'light' | 'strong' | 'code' }) => string;
  getStatusText: (status: MoldOrderStatus, orderType?: MoldOrderType, orderWorkflowDefinition?: OrderWorkflowDefinition | null) => string;
  getStatusIcon: (status: MoldOrderStatus, orderType?: MoldOrderType, orderWorkflowDefinition?: OrderWorkflowDefinition | null) => React.ComponentType | null;
  isAllowSetNextAssignee: (status: MoldOrderStatus, orderType: MoldOrderType) => boolean;
  getStatusDueDateBehavior: (status: MoldOrderStatus, orderType: MoldOrderType) => StatusDueDateBehavior | undefined;
  getDefaultNextAssignee: (moldOrder: MoldOrder) => User | undefined;
  getMoldOrderGroups: () => ExtendedMoldOrderFormConfig[];
  getMoldOrderCreationConfig: (type: MoldOrderFirstLevelType) => ExtendedMoldOrderFormConfig | null;
  toMoldOrderGroup: (moldOrderType: MoldOrderType) => ExtendedMoldOrderFormConfig | null;
  getMoldDisplayFieldGroups: () => DisplayFieldsGroup[];
  getHeaderStyleConfig: (type: MoldOrderFirstLevelType | null) => {
    headerBgColor: string;
    headerTextColor: string;
    headerBorderColor: string;
    headerShadow: string;
    uiConfig: UIConfig | null;
    colorCode: string;
  };
  isRepairOrderType: (orderType: MoldOrderType) => boolean;
  isMaintenanceOrder: (orderType: MoldOrderType) => boolean;
  isMoldAcceptanceOrder: (orderType: MoldOrderType) => boolean;
  getBoardColumns: (boardId: string) => BoardColumn[];
  getKanbanCardHeadlines: (moldOrder: MoldOrder) => KanbanCardHeadlines;
  getKanbanCardConfig: () => { titleField: string; subtitleField: string };
  getTaskRowConfig: () => { infoField: string };
  getShiftConfig: () => { dayStartHour: number; dayEndHour: number };
  getClientSpecificMoldFields: () => DisplayFieldConfig[];
  getMoldDisplayFieldValues: (mold: Mold) => { title: string; subtitle: string };
  getVisibleMoldColumns: () => GenericColumn<Mold>[];
  getMoldSearchFields: () => SearchFieldConfig[];
  getBoards: () => BoardConfig[];
  getActionButtonMapping: (firstLevelType: MoldOrderFirstLevelType, status: MoldOrderStatus) => ActionButtonMapping | undefined;
  getRelatedOrderTypes: (orderType: MoldOrderType) => RelatedOrderTypeConfig[];
  getNextStepActionConfig: (orderType: MoldOrderType, status: MoldOrderStatus) => FormFieldConfig[];
}


const AppStateContext = createContext<AppStateContextValue | undefined>(undefined);


export const AppStateProvider: React.FC<AppStateProviderProps> = ({ companyCode, children }) => {
  const appState = useMemo(() => getAppState(companyCode), [companyCode]);

  useEffect(() => {
    if (appState.appName) {
      document.title = `${appState.appName} - ระบบจัดการแม่พิมพ์`;
    }
  }, [appState.appName]);

  const getFeatureToggle = (): FeatureToggles => {
    const stage = (process.env.VITE_STAGE || 'production') as keyof typeof appState.featureToggles;
    return appState.featureToggles[stage];
  }

  // getFormFields uses the current appState and applies filtering for repair types
  const getFormFields = (moldOrderType: string) => {
    const formConfigs = appState.moldOrders[moldOrderType]?.formConfigs || [];
    const featureToggles = getFeatureToggle();

    return formConfigs.map(field => {
      if (field.key === 'moldOrderType' && field.type === 'select' && field.options) {
        return {
          ...field,
          options: filterMoldOrderTypesByToggle(field.options as any[], featureToggles)
        };
      }
      return field;
    });
  };

  // Filter mold order types based on feature toggles
  const getFilteredMoldOrderTypesForRepair = () => {
    const featureToggles = getFeatureToggle();
    return filterMoldOrderTypesByToggle(moldOrderTypesForRepair, featureToggles);
  };

  const getCurrentOrderTemplateVersion = (firstLevelType: MoldOrderFirstLevelType): string => {
    return appState.moldOrders[firstLevelType]?.orderTemplateVersion || '1.0';
  };

  const getMoldOrderMainTypes = (firstLevelType: MoldOrderFirstLevelType): MoldOrderMainTypeStruct[] => {
    const mainTypes = appState.moldOrders[firstLevelType]?.moldOrderMainTypes || [];
    const featureToggles = getFeatureToggle();

    return filterMoldOrderMainTypesByToggle(mainTypes, featureToggles);
  };

  const getOrderMainTypeLabel = (type: MoldOrderFirstLevelType, mainType: MoldOrderMainType) => {
    const mainTypes = appState.moldOrders[type]?.moldOrderMainTypes || [];
    const mainTypeStruct = mainTypes.find(mt => mt.value === mainType);
    return mainTypeStruct ? mainTypeStruct.label : 'ไม่ทราบประเภท';
  };

  const isInMoldOrderMainType = (type: MoldOrderMainType, subType: MoldOrderType): boolean => {
    const firstLevelType = getMoldOrderMainType(subType);
    const moldOrderMainTypes = getMoldOrderMainTypes(firstLevelType);
    const mainType = moldOrderMainTypes.find((t) => t.value === type);
    if (!mainType) {
      return true;
    }
    return mainType?.subTypes.includes(subType) || false;
  };

  const getMoldOrderMainTypeLabel = (type: MoldOrderFirstLevelType) => {
    switch (type) {
      case 'maintenance':
        return appState.moldOrders.maintenance?.title || 'ล้างแม่พิมพ์';
      case 'repair':
        return appState.moldOrders.repair?.title || 'ซ่อมแม่พิมพ์';
      case 'mold_acceptance':
        return appState.moldOrders.mold_acceptance?.title || 'รับเข้าแม่พิมพ์';
      default:
        return appState.moldOrders[type].title || 'ไม่ทราบประเภท';
    }
  }

  const getStatusMapping = (type: MoldOrderFirstLevelType) => {
    return appState.moldOrders[type]?.statusMapping || {};
  }

  const getMoldOrderMainType = (type: MoldOrderType | MoldOrderMainType): MoldOrderFirstLevelType => {
    return getMoldOrderFirstLevelType(type);
  }

  const getMoldFormConfiguration = (): Record<string, FormFieldConfig[]> => {
    return appState.mold.formConfiguration || {};
  }

  const getMoldOrderTypeLabel = (type: MoldOrderType): string => {
    try {
      return getOrderTypeLabel(type);
    } catch (error) {
      console.error(error);
      return 'ไม่ทราบประเภท';
    }
  }

  const getDisplayMoldOrderFields = (moldOrderType: MoldOrderType): DisplayFieldConfig[] => {
    const fieldMap: Record<string, DisplayFieldConfig[]> = {
      'mold_acceptance': fieldsForMoldAcceptance,
      'repair.MINOR': fieldsForMinorRepair,
      'repair.MAJOR': fieldsForMinorRepair,
      'repair': fieldsForRepair,
      'modify': fieldsForModify,
      'mold_export': fieldsForMoldExport,
    };

    const orderSpecificFields = fieldMap[moldOrderType] || defaultFields;

    return [
      ...getClientSpecificMoldFields(),
      ...orderSpecificFields
    ];
  }

  const isMoldOrderCheckedForFormCompletion = (orderType: MoldOrderType): boolean => {
    const mainType = getMoldOrderMainType(orderType);
    if (process.env.NODE_ENV === 'development') {
      return false;
    }
    return appState.moldOrders[mainType]?.checkForFormCompletion ?? false;
  };

  /**
   * Get status color classes for a mold order status
   * @param status - The mold order status
   * @param orderType - Optional order type for context-specific colors
   * @param options - Configuration options
   * @param options.variant - Color variant: 'light' (default), 'strong', or 'code'
   *   - 'light': Returns light background colors (e.g., 'bg-green-100 text-green-800')
   *   - 'strong': Returns stronger/darker colors (e.g., 'bg-green-500 text-green-800')  
   *   - 'code': Returns just the color name (e.g., 'green')
   * @returns CSS class string or color code
   * 
   * @example
   * // Light variant (default)
   * getStatusColor(status, orderType) // 'bg-blue-100 text-blue-800'
   * 
   * // Strong variant for emphasis
   * getStatusColor(status, orderType, { variant: 'strong' }) // 'bg-blue-500 text-blue-800'
   * 
   * // Color code only
   * getStatusColor(status, orderType, { variant: 'code' }) // 'blue'
   */
  const getStatusColor = (
    status: MoldOrderStatus,
    orderType: MoldOrderType,
    options: { variant?: 'light' | 'strong' | 'code' } = {}
  ): string => {
    const { variant = 'light' } = options;
    const mainType = getMoldOrderMainType(orderType);
    const colorCodeMapping = appState.moldOrders[mainType]?.colorCodeMapping?.all || {};
    const colorCode = colorCodeMapping[status] || 'gray';

    switch (variant) {
      case 'light':
        return `bg-${colorCode}-100 text-${colorCode}-800`;
      case 'strong':
        return `bg-${colorCode}-500 text-${colorCode}-800`;
      case 'code':
        return colorCode;
      default:
        return `bg-${colorCode}-100 text-${colorCode}-800`;
    }
  };

  const getStatusText = (
    status: MoldOrderStatus,
    orderType?: MoldOrderType,
    orderWorkflowDefinition?: OrderWorkflowDefinition | null
  ): string => {
    if (orderType) {
      const mainType = getMoldOrderMainType(orderType);
      const statusMapping = getStatusMapping(mainType);
      const workflowVersion = orderWorkflowDefinition?.version;
      const mappingKey = Object.keys(statusMapping).find(key => parseInt(key) === workflowVersion) ?? 'all';
      return statusMapping[mappingKey as keyof typeof statusMapping][status] || status;
    }
    return status;
  };

  const getStatusIcon = (
    status: MoldOrderStatus,
    orderType?: MoldOrderType,
    orderWorkflowDefinition?: OrderWorkflowDefinition | null
  ): React.ComponentType | null => {
    if (orderType) {
      const mainType = getMoldOrderMainType(orderType);
      const iconStatusMapping = appState.moldOrders[mainType]?.iconStatusMapping || {};
      const workflowVersion = orderWorkflowDefinition?.version;
      const mappingKey = Object.keys(iconStatusMapping).find(key => parseInt(key) === workflowVersion) ?? 'all';
      const iconName = iconStatusMapping[mappingKey as keyof typeof iconStatusMapping]?.[status] || 'FileText';
      return getDynamicIcon({ name: iconName, source: 'lucide' });
    }
    return getDynamicIcon({ name: 'FileText', source: 'lucide' });
  }

  const isAllowSetNextAssignee = (status: MoldOrderStatus, orderType: MoldOrderType): boolean => {
    const mainType = getMoldOrderMainType(orderType);
    const disallowSetNextAssignee = appState.moldOrders[mainType]?.disallowSetNextAssignee?.all ?? [];
    return !disallowSetNextAssignee.includes(status)
  };

  const getStatusDueDateBehavior = (
    status: MoldOrderStatus,
    orderType: MoldOrderType
  ): StatusDueDateBehavior | undefined => {
    const mainType = getMoldOrderMainType(orderType);
    return appState.moldOrders[mainType]?.statusDueDateBehavior?.all?.[status];
  };

  const getDefaultNextAssignee = (moldOrder: MoldOrder): User | undefined => {
    if (!isAllowSetNextAssignee(moldOrder.status, moldOrder.type)) {
      return moldOrder.assignedTo;
    }
    return moldOrder.nextAssignableUsers[0];
  }

  const getMoldStatusLabel = (mold: Mold): string => {
    const { mold: { statusLabelMapping } } = appState;
    return statusLabelMapping[mold.status] || mold.status;
  }

  const getMoldOrderGroups = (): ExtendedMoldOrderFormConfig[] => {
    const { moldOrders } = appState;
    const featureToggles = getFeatureToggle();

    return Object.keys(moldOrders).map(key => {
      const moldOrderConfig = moldOrders[key as MoldOrderFirstLevelType];
      const isEnabled = resolveEnabledStatus(moldOrderConfig?.isEnable, featureToggles);

      return {
        ...moldOrderConfig,
        key,
        isEnabled,
        moldOrderTypes: moldOrderConfig?.moldOrderTypes || []
      };
    });
  };

  /**
   * Get specific mold order configuration by type
   */
  const getMoldOrderCreationConfig = (type: MoldOrderFirstLevelType): ExtendedMoldOrderFormConfig | null => {
    const { moldOrders } = appState;
    const featureToggles = getFeatureToggle();
    const moldOrderConfig = moldOrders[type];
    
    if (!moldOrderConfig) {
      return null;
    }

    const isEnabled = resolveEnabledStatus(moldOrderConfig?.isEnable, featureToggles);

    return {
      ...moldOrderConfig,
      key: type,
      isEnabled,
      moldOrderTypes: moldOrderConfig?.moldOrderTypes || []
    };
  };

  const getBoards = () => {
    return appState.kanban.boards.data || [];
  }

  /**
   * Get header style configuration from board's uiConfig
   */
  const getHeaderStyleConfig = (type: MoldOrderFirstLevelType | null) => {
    if (!type) {
      return {
        headerBgColor: 'bg-gray-600',
        headerTextColor: 'text-black',
        headerBorderColor: 'border-gray-500',
        headerShadow: 'shadow-md',
        uiConfig: null,
        colorCode: 'gray',
      };
    }

    // Find the board configuration for this type
    // const board = appState.kanban.boards.data.find(b => b.group === type);
    const board = appState.moldOrders[type];
    
    if (!board || !board.uiConfig?.header) {
      // Fallback to default styles
      return {
        headerBgColor: 'bg-gray-600',
        headerTextColor: 'text-black',
        headerBorderColor: 'border-gray-500',
        headerShadow: 'shadow-md',
        uiConfig: null,
        colorCode: 'gray',
      };
    }

    // Return the header config from uiConfig, including the full uiConfig for icon access
    return {
      headerBgColor: board.uiConfig.header.bgColor,
      headerTextColor: board.uiConfig.header.textColor,
      headerBorderColor: board.uiConfig.header.borderColor,
      headerShadow: board.uiConfig.header.shadow,
      uiConfig: board.uiConfig,
      colorCode: board.uiConfig.colorCode || 'gray',
    };
  };

  const getMoldDisplayFieldGroups = (): DisplayFieldsGroup[] => {
    return appState.mold.displayFieldGroups || [];
  }

  const getMoldTitle = (mold: Mold): string => {
    const primaryField = appState.mold.displayFields.primaryField;
    return getMoldFieldValueFromMold(mold, primaryField);
  }

  const getBoardColumns = (boardId: string): BoardColumn[] => {
    return appState.kanban.boards.data.find(board => board.key === boardId)?.columns || [];
  }

  const getKanbanCardConfig = () => {
    return {
      titleField: appState.kanban.card.title,
      subtitleField: appState.kanban.card.subtitle,
    };
  }

  const getTaskRowConfig = () => {
    return {
      infoField: appState.dashboard.taskRow.infoField,
    };
  }

  const getShiftConfig = (): { dayStartHour: number; dayEndHour: number } => {
    return appState.dashboard.shifts ?? { dayStartHour: 8, dayEndHour: 20 };
  }

  const getMoldDisplayFields = () => {
    return appState.mold.displayFields;
  }

  const getClientSpecificMoldFields = (): DisplayFieldConfig[] => {
    const { primaryField, secondaryField } = appState.mold.displayFields;

    // Field renderer - maps field names to their display logic
    const renderField = (fieldName: MoldDisplayFieldType, isLink = false) => {
      const fieldRenderers: Partial<Record<MoldDisplayFieldType, (order: MoldOrder) => React.ReactNode>> = {
        sbiId: (order) => order.mold.sbiId,
        moldCode: (order) => order.mold.moldCode || order.mold.sbiId,
        moldName: (order) => <span className="font-medium">{order.mold.moldName || '-'}</span>,
        productParts: (order) => (
          <ProductPartsCell
            parts={order.mold.productParts}
            useGridLayout={true}
            bgColor='bg-blue-100'
          />
        ),
      };

      const renderer = fieldRenderers[fieldName];
      if (!renderer) {
        // Fallback for any Mold property not explicitly handled
        return (order: MoldOrder) => {
          const value = order.mold[fieldName];
          return String(value ?? '-');
        };
      }

      // Wrap in link if it's a primary field
      if (isLink && (fieldName === 'sbiId' || fieldName === 'moldCode')) {
        return (order: MoldOrder) => (
          <Link to={`${ROUTES.MOLDS}/${order.mold.id}`} className="text-blue-500 hover:text-blue-600 underline">
            <span className="font-medium">{renderer(order)}</span>
          </Link>
        );
      }

      return renderer;
    };

    // Field labels mapping - now type-safe
    const getFieldLabel = (fieldName: MoldDisplayFieldType): string => {
      const labels: Partial<Record<MoldDisplayFieldType, string>> = {
        sbiId: 'รหัสแม่พิมพ์',
        moldCode: 'Mold Code',
        moldName: 'Mold Name',
        productParts: 'รหัสชิ้นส่วน',
      };
      return labels[fieldName] || fieldName;
    };

    return [
      {
        label: getFieldLabel(primaryField),
        getValue: renderField(primaryField, true),
      },
      {
        label: getFieldLabel(secondaryField),
        getValue: renderField(secondaryField, false),
      }
    ];
  }

  // Shared helper to get field value from mold
  const getMoldFieldValue = (moldOrder: MoldOrder, field: string): string => {
    switch (field) {
      case 'sbiId':
        return moldOrder.mold.sbiId ?? '';
      case 'moldCode':
        return moldOrder.mold.moldCode ?? '';
      case 'productParts':
        return moldOrder.mold.productParts.map(p => p.partNumber).join(', ') ?? '';
      default:
        return moldOrder.mold[field as keyof Mold] as string || '';
    }
  }

  // Helper to get field value directly from mold (for components that work with Mold objects)
  const getMoldFieldValueFromMold = (mold: Mold, field: string): string => {
    switch (field) {
      case 'sbiId':
        return mold.sbiId ?? '';
      case 'moldCode':
        return mold.moldCode ?? '';
      case 'productParts':
        return mold.productParts.map(p => p.partNumber).join(', ') ?? '';
      default:
        return mold[field as keyof Mold] as string || '';
    }
  }

  // Helper to get primary and secondary field values for mold display
  const getMoldDisplayFieldValues = (mold: Mold) => {
    const { primaryField, secondaryField } = appState.mold.displayFields;
    return {
      title: getMoldFieldValueFromMold(mold, primaryField),
      subtitle: getMoldFieldValueFromMold(mold, secondaryField),
    };
  }

  const getKanbanCardHeadlines = (moldOrder: MoldOrder): KanbanCardHeadlines => {
    const titleField = appState.kanban.card.title;
    const subtitleField = appState.kanban.card.subtitle;

    return {
      title: getMoldFieldValue(moldOrder, titleField),
      subtitle: getMoldFieldValue(moldOrder, subtitleField),
    }
  }

  const getMoldOrderCardHeadlines = (moldOrder: MoldOrder): KanbanCardHeadlines => {
    const titleField = appState.moldOrderDetail.header.title;
    const subtitleField = appState.moldOrderDetail.header.subtitle;

    return {
      title: getMoldFieldValue(moldOrder, titleField),
      subtitle: getMoldFieldValue(moldOrder, subtitleField),
    }
  }

  /*** For mold order creation page */
  const getVisibleMoldColumns = () => {
    return appState.moldOrderCreation.moldTable.visibleColumns;
  }

  const getMoldSearchFields = () => {
    return appState.search.moldSearchFields.availableSearchFields;
  }

  // Helper function to resolve enabled status based on feature toggles
  const resolveEnabledStatus = (
    enableConfig: boolean | string | undefined,
    featureToggles: FeatureToggles
  ): boolean => {
    if (typeof enableConfig === 'string') {
      return featureToggles[enableConfig as keyof FeatureToggles] ?? false;
    }
    return enableConfig ?? false;
  };

  const toMoldOrderGroup = (moldOrderType: MoldOrderType): ExtendedMoldOrderFormConfig | null => {
    const moldOrderGroups = getMoldOrderGroups();
    for (const group of moldOrderGroups) {
      if (group.moldOrderTypes.includes(moldOrderType) && group.isEnabled) {
        return group;
      }
    }
    return null
  }

  const isRepairOrderType = (orderType: MoldOrderType): boolean => {
    const repairGroup = toMoldOrderGroup(orderType);
    return repairGroup?.key === 'repair';
  }

  const isMaintenanceOrder = (orderType: MoldOrderType): boolean => {
    const maintenanceGroup = toMoldOrderGroup(orderType);
    return maintenanceGroup?.key === 'maintenance';
  }

  const isMoldAcceptanceOrder = (orderType: MoldOrderType): boolean => {
    const acceptanceGroup = toMoldOrderGroup(orderType);
    return acceptanceGroup?.key === 'mold_acceptance';
  }

  const getActionButtonMapping = (firstLevelType: MoldOrderFirstLevelType, status: MoldOrderStatus): ActionButtonMapping | undefined => {
    return appState.moldOrders[firstLevelType]?.actionButtonMapping?.[status];
  }

  /**
   * Related orders of a DIFFERENT type this order may open — the configured add-on to
   * the default "reopen the same type after a rejection". Keyed by the order's own
   * sub-type, so sibling sub-types of one first-level type can differ.
   */
  const getRelatedOrderTypes = (orderType: MoldOrderType): RelatedOrderTypeConfig[] => {
    const firstLevelType = getMoldOrderMainType(orderType);
    return appState.moldOrders[firstLevelType]?.relatedOrderTypes?.[orderType] ?? [];
  }

  const getNextStepActionConfig = (orderType: MoldOrderType, status: MoldOrderStatus): FormFieldConfig[] => {
    const firstLevelType = getMoldOrderMainType(orderType);
    return appState.moldOrders[firstLevelType]?.nextStepAction?.[status]?.formConfigs || [];
  }

  const contextValue = useMemo(() => ({
    ...appState,
    companyCode,
    featureToggles: getFeatureToggle(),
    getMoldFieldValue,
    getMoldStatusLabel,
    getMoldDisplayFields,
    getMoldOrderCardHeadlines,
    getFormFields,
    getCurrentOrderTemplateVersion,
    getOrderMainTypeLabel,
    isInMoldOrderMainType,
    getMoldOrderMainTypes,
    getMoldOrderMainTypeLabel,
    getMoldOrderTypeLabel,
    getMoldOrderMainType,
    getDisplayMoldOrderFields,
    getStatusColor,
    getStatusText,
    getStatusIcon,
    getDefaultNextAssignee,
    getFilteredMoldOrderTypesForRepair,
    getMoldOrderGroups,
    getMoldOrderCreationConfig,
    getMoldDisplayFieldGroups,
    getMoldTitle,
    getBoardColumns,
    getKanbanCardHeadlines,
    getKanbanCardConfig,
    getTaskRowConfig,
    getShiftConfig,
    getClientSpecificMoldFields,
    getMoldDisplayFieldValues,
    getVisibleMoldColumns,
    getMoldSearchFields,
    toMoldOrderGroup,
    isAllowSetNextAssignee,
    getStatusDueDateBehavior,
    isMoldOrderCheckedForFormCompletion,
    isMoldAcceptanceOrder,
    isMaintenanceOrder,
    isRepairOrderType,
    getMoldFormConfiguration,
    getBoards,
    getHeaderStyleConfig,
    getActionButtonMapping,
    getRelatedOrderTypes,
    getNextStepActionConfig,
  }), [appState, companyCode]);

  return (
    <AppStateContext.Provider value={contextValue}>
      {children}
    </AppStateContext.Provider>
  );
};


export const useAppState = (): AppStateContextValue => {
  const context = useContext(AppStateContext);
  if (!context) {
    throw new Error('useAppState must be used within an AppStateProvider');
  }
  return context;
};
